import * as React from "react";
import { crearClienteAuth, entrarConGoogle, salir, tokenActual, type ClienteAuth } from "@iavmx/auth/navegador";
import { crearApi, devPersona, fijarDevPersona, leer, type Api } from "./api";

export interface Configuracion {
  hostPropuestas: string;
  supabaseUrl: string | null;
  supabaseAnonKey: string | null;
  urlPortal: string;
  /** Solo local: la API actúa como DEV_PERSONA sin sesión de Supabase. */
  dev: boolean;
}

interface Sesion {
  cargando: boolean;
  configuracion: Configuracion | null;
  correo: string | null;
  /** Hay con qué llamar a la API: sesión real o modo de desarrollo. */
  listo: boolean;
  dev: boolean;
  /** Solo local: correo con el que se actúa (x-dev-persona) o null para DEV_PERSONA. */
  actuandoComo: string | null;
  actuarComo: (correo: string | null) => void;
  api: Api;
  entrar: () => Promise<void>;
  cerrar: () => Promise<void>;
}

const Contexto = React.createContext<Sesion | null>(null);

// Un solo cliente de Auth por pestaña; el API lo lee al pedir el token, no durante el render.
let clienteActual: ClienteAuth | null = null;
const api: Api = crearApi(async () => (clienteActual ? tokenActual(clienteActual) : null));

export function SesionProveedor({ children }: { children: React.ReactNode }) {
  const [configuracion, setConfiguracion] = React.useState<Configuracion | null>(null);
  const [cliente, setCliente] = React.useState<ClienteAuth | null>(null);
  const [correo, setCorreo] = React.useState<string | null>(null);
  const [cargando, setCargando] = React.useState(true);
  const [actuandoComo, setActuandoComo] = React.useState<string | null>(() => devPersona());

  React.useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const c = await leer<Configuracion>(await fetch(`/api/configuracion${window.location.search}`));
        if (cancelado) return;
        setConfiguracion(c);
        if (c.supabaseUrl && c.supabaseAnonKey) {
          const cl = crearClienteAuth(c.supabaseUrl, c.supabaseAnonKey);
          clienteActual = cl;
          setCliente(cl);
          const { data } = await cl.auth.getSession();
          setCorreo(data.session?.user.email ?? null);
          cl.auth.onAuthStateChange((_e, s) => setCorreo(s?.user.email ?? null));
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  const dev = Boolean(configuracion?.dev);
  const valor = React.useMemo<Sesion>(
    () => ({
      cargando,
      configuracion,
      correo,
      dev,
      listo: !cargando && (Boolean(correo) || dev),
      actuandoComo,
      actuarComo: (c) => {
        fijarDevPersona(c);
        setActuandoComo(c);
      },
      api,
      entrar: async () => {
        if (!cliente) throw new Error("Auth sin configurar.");
        await entrarConGoogle(cliente, window.location.origin);
      },
      cerrar: async () => {
        if (cliente) await salir(cliente);
      },
    }),
    [cargando, configuracion, correo, cliente, dev, actuandoComo],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useSesion(): Sesion {
  const s = React.useContext(Contexto);
  if (!s) throw new Error("useSesion necesita <SesionProveedor>.");
  return s;
}
